import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import api from '@/admin/lib/api'
import { useOrg } from '@/admin/hooks/use-org'

/**
 * Role-scoped member attributes.
 *
 * A role (e.g. Resolution Specialist) can define attributes (e.g. geozone)
 * that every holder of the role then sets a value for on their member profile.
 * These are ordinary ``MembershipAttribute`` definitions with
 * ``applies_to_role`` set - the same backend surface the tenant-wide member
 * fields use (``/member-attributes/``), scoped to one role via ``?role=``.
 *
 * The backend wraps every payload as ``{ status, message, result }``, so we
 * read ``r.data.result ?? r.data`` throughout.
 */

export interface RoleAttribute {
  uuid: string
  code_name: string
  display_name: string
  description: string
  field_type: string
  configuration_data: Record<string, unknown>
  required: boolean
  editable: boolean
  order: number
  applies_to_role: string | null
  applies_to_role_name: string | null
}

export interface RoleAttributeInput {
  code_name: string
  display_name: string
  description?: string
  field_type: string
  configuration_data?: Record<string, unknown>
  required?: boolean
  order?: number
}

const roleAttributesKey = (orgUuid: string | undefined, roleUuid: string) =>
  ['role-attributes', orgUuid, roleUuid] as const

export function useRoleAttributes(roleUuid: string | undefined) {
  const { orgUuid } = useOrg()
  return useQuery<RoleAttribute[]>({
    queryKey: roleAttributesKey(orgUuid, roleUuid ?? ''),
    queryFn: () =>
      api
        .get(`/organisations/${orgUuid}/member-attributes/`, {
          params: { role: roleUuid },
        })
        .then((r) => (r.data.result ?? r.data) as RoleAttribute[]),
    enabled: !!orgUuid && !!roleUuid,
    staleTime: 30_000,
  })
}

export function useCreateRoleAttribute(roleUuid: string | undefined) {
  const { orgUuid } = useOrg()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: RoleAttributeInput) =>
      api
        .post(`/organisations/${orgUuid}/member-attributes/`, {
          ...input,
          applies_to_role: roleUuid,
        })
        .then((r) => (r.data.result ?? r.data) as RoleAttribute),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: roleAttributesKey(orgUuid, roleUuid ?? ''),
      })
    },
  })
}

export function useUpdateRoleAttribute(roleUuid: string | undefined) {
  const { orgUuid } = useOrg()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({
      codeName,
      input,
    }: {
      codeName: string
      input: Partial<RoleAttributeInput>
    }) =>
      api
        .patch(
          `/organisations/${orgUuid}/member-attributes/${codeName}/`,
          input,
        )
        .then((r) => (r.data.result ?? r.data) as RoleAttribute),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: roleAttributesKey(orgUuid, roleUuid ?? ''),
      })
    },
  })
}

export function useDeleteRoleAttribute(roleUuid: string | undefined) {
  const { orgUuid } = useOrg()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (codeName: string) =>
      api.delete(`/organisations/${orgUuid}/member-attributes/${codeName}/`),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: roleAttributesKey(orgUuid, roleUuid ?? ''),
      })
    },
  })
}
